import type { KeyboardEvent, SyntheticEvent } from 'react';

/**
 * Makes a non-interactive element operable from the keyboard (WCAG 2.1.1).
 *
 * A `<div>` with an `onClick` is invisible to keyboard and switch users: it
 * takes no focus and responds to no key. This gives it the three things a
 * control needs — a role, a tab stop, and Enter/Space activation.
 *
 * Spread it *first* so anything the element declares itself still wins:
 *
 *     <div {...activate(select)} role="option" aria-selected={isSelected}>
 *
 * Space is prevented from scrolling the page, which is what a real button does.
 *
 * Prefer an actual `<button>` where the markup allows it. This exists for the
 * cases where it does not — a row, a card, a grid cell — not as a shortcut
 * around using the right element.
 */
export const activate = <E extends SyntheticEvent>(handler: (event: E) => void) => ({
  role: 'button',
  tabIndex: 0,
  onClick: handler,
  onKeyDown: (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handler(event as unknown as E);
  },
});
